import React, { useCallback, useEffect, useMemo, useState } from 'react';

function decodeInput(value) {
  let normalized = String(value || '').trim();
  if (!normalized) return '';

  for (let i = 0; i < 2; i += 1) {
    try {
      const decoded = decodeURIComponent(normalized);
      if (decoded === normalized) break;
      normalized = decoded;
    } catch {
      break;
    }
  }

  return normalized;
}

function extractToken(value) {
  const raw = decodeInput(value);
  if (!raw) return '';

  if (raw.startsWith('#edit=')) {
    return raw.slice('#edit='.length).trim();
  }

  if (raw.includes('#edit=')) {
    const idx = raw.indexOf('#edit=');
    return raw.slice(idx + '#edit='.length).trim();
  }

  try {
    const url = new URL(raw);
    const hash = url.hash || '';
    if (hash.startsWith('#edit=')) {
      return hash.slice('#edit='.length).trim();
    }
    const params = new URLSearchParams(url.search || '');
    const qp = params.get('edit');
    if (qp) return qp.trim();
  } catch {
    // Not a URL, treat as raw token
  }

  return raw;
}

export default function EditTokenSheet({
  isOpen,
  onClose,
  onEnable,
  viewOnlyUrl,
  editableUrlHint
}) {
  const [value, setValue] = useState('');
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!isOpen) {
      setValue('');
      setError(null);
    }
  }, [isOpen]);

  const token = useMemo(() => extractToken(value), [value]);

  const handleSubmit = useCallback((e) => {
    e.preventDefault();
    const trimmed = token.trim();
    if (!trimmed) {
      setError('Please paste your edit token (or the editable link from your email).');
      return;
    }
    setError(null);
    onEnable?.(trimmed);
    onClose?.();
  }, [onClose, onEnable, token]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center sm:items-center sm:p-4">
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      <div className="relative w-full max-w-lg rounded-t-lg bg-slate-900 shadow-2xl sm:rounded-lg">
        <div className="border-b border-white/10 px-6 py-4">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-xl font-bold text-white">Enable day edits</h2>
              <p className="mt-1 text-sm text-white/70">
                Paste the edit token or the editable link from your purchase email.
              </p>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg p-1.5 text-white/60 transition-colors hover:bg-white/10 hover:text-white"
              aria-label="Close"
            >
              <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div className="px-6 py-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label htmlFor="edit-token" className="mb-2 block text-sm font-semibold text-white">
                Edit token or editable link
              </label>
              <input
                id="edit-token"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                className="w-full rounded-lg border border-white/20 bg-slate-800/60 px-4 py-3 text-white placeholder-white/40 shadow-inner focus:border-emerald-400/60 focus:outline-none focus:ring-2 focus:ring-emerald-400/40"
                placeholder={editableUrlHint || 'https://alakaialoha.com/itinerary/...#edit=...'}
                autoComplete="off"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
              {viewOnlyUrl && (
                <p className="mt-2 text-xs text-white/60">
                  View-only link: <span className="text-white/80">{viewOnlyUrl}</span>
                </p>
              )}
            </div>

            {error && (
              <div className="rounded-lg border border-red-400/40 bg-red-500/10 px-4 py-3 text-sm text-red-200">
                {error}
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
              <button type="button" className="btn-secondary w-full sm:w-auto" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn-primary-cta w-full sm:w-auto" disabled={!token.trim()}>
                Enable editing
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
